import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import ServiceWrapper from "../../components/ServiceWrapper";
import { api } from "../../utils/api";
import { track } from "../../utils/track";
import { C, Page, Lead, Primary, Ghost, Chip, Note, Err } from "./styles";
import { noPurchaseUI } from "../../utils/billing";

export interface IMyScoreRow {
  id: number;
  title: string;
  singer: string;
  status: "queued" | "processing" | "done" | "failed";
  visibility: "private" | "link" | "group";
  error?: string | null;
  created_at: string;
  owner_nickname?: string;
}

export interface IQuota {
  plan: "subscriber" | "free";
  used: number;
  limit: number;
  left: number;
  max_mb: number;
  max_sec: number;
  waiting: number;
}

export const statusChip = (s: IMyScoreRow["status"]) =>
  s === "done" ? <Chip tone="ok">완성</Chip>
    : s === "failed" ? <Chip tone="err">만들지 못함</Chip>
      : s === "processing" ? <Chip tone="warn">만드는 중</Chip>
        : <Chip tone="dim">기다리는 중</Chip>;

const visText = { private: "나만 보기", link: "링크 공유", group: "단체 공유" };

/** 내 악보 목록 (/my-scores): 내가 만든 악보 + 단체에서 공유받은 악보 */
const MyScoreList: React.FC = () => {
  const history = useHistory();
  const [mine, setMine] = useState<IMyScoreRow[] | null>(null);
  const [shared, setShared] = useState<IMyScoreRow[]>([]);
  const [quota, setQuota] = useState<IQuota | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: l }, { data: q }] = await Promise.all([api.get("/my-scores"), api.get("/my-scores/quota")]);
      setMine(l.data.mine);
      setShared(l.data.shared);
      setQuota(q.data);
      setErr("");
    } catch (e) {
      setErr("목록을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // 만드는 중인 곡이 있으면 20초마다 새로 확인
  const pending = !!mine && mine.some((m) => m.status === "queued" || m.status === "processing");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [pending, load]);

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>내 악보 만들기</h1>
        <Lead>직접 부르거나 연주한 음원을 올리면 AI 가 코드 악보로 만들어 드려요. 만든 악보는 나만 보거나, 링크·단체로 나눌 수 있어요.</Lead>

        {quota && (
          <QuotaBox>
            <div>
              {quota.plan === "free"
                ? quota.left > 0 ? <span>무료로 <b>{quota.left}곡</b> 만들어 볼 수 있어요</span> : <span>무료 체험을 쓰셨어요</span>
                : <span>이번 달 <b>{quota.left}곡</b> 더 만들 수 있어요 <em>({quota.used}/{quota.limit})</em></span>}
            </div>
            {quota.plan === "free" && quota.left <= 0
              ? noPurchaseUI()
                ? <Note>이용권이 있는 계정이면 한 달에 {quota.limit > 1 ? quota.limit : 20}곡까지 만들 수 있어요.</Note>
                : <Primary type="button" onClick={() => history.push("/pricing")}>구독하고 더 만들기</Primary>
              : <Primary type="button" disabled={quota.left <= 0} onClick={() => { track("myscore:new"); history.push("/my-scores/new"); }}>+ 새 악보 만들기</Primary>}
          </QuotaBox>
        )}
        {err && <Err>{err}</Err>}

        <SubHead>
          <h2>내가 만든 악보</h2>
          <button type="button" onClick={() => history.push("/my-scores/groups")}>단체 관리</button>
        </SubHead>
        {mine && !mine.length && <Empty>아직 만든 악보가 없어요.</Empty>}
        <List>
          {(mine || []).map((m) => (
            <li key={m.id}>
              <button type="button" onClick={() => history.push(`/my-scores/${m.id}`)}>
                <div>
                  <strong>{m.title}</strong>
                  <span>{[m.singer, m.status === "done" ? visText[m.visibility] : "", m.created_at.slice(0, 10)].filter(Boolean).join(" · ")}</span>
                </div>
                {statusChip(m.status)}
              </button>
            </li>
          ))}
        </List>
        {pending && <Note style={{ marginTop: 8 }}>보통 10분 안에 완성돼요. 다 되면 알림으로 알려 드려요.</Note>}

        {shared.length > 0 && (
          <>
            <SubHead><h2>단체에서 공유받은 악보</h2></SubHead>
            <List>
              {shared.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => history.push(`/my-scores/${m.id}`)}>
                    <div>
                      <strong>{m.title}</strong>
                      <span>{[m.singer, m.owner_nickname && `${m.owner_nickname} 님`].filter(Boolean).join(" · ")}</span>
                    </div>
                  </button>
                </li>
              ))}
            </List>
          </>
        )}
        <Note style={{ marginTop: 24 }}>
          AI 가 만든 악보라 틀린 곳이 있을 수 있어요. 악보 화면에서 <b>고치기</b>로 바로잡을 수 있어요.
        </Note>
      </Page>
    </ServiceWrapper>
  );
};

export default MyScoreList;

const QuotaBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
  & > div {
    font-size: 15px;
    color: ${C.muted};
  }
  & b {
    color: ${C.fg};
  }
  & em {
    font-style: normal;
    color: ${C.dim};
  }
`;

const SubHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 26px 0 10px;
  & > h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${C.muted};
  }
  & > button {
    border: 0;
    background: none;
    color: ${C.link};
    font-size: 14px;
    cursor: pointer;
    padding: 6px 0;
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 18px 0;
  color: ${C.dim};
  font-size: 15px;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${C.line};
  border-radius: 14px;
  overflow: hidden;
  &:empty {
    display: none;
  }
  & > li + li {
    border-top: 1px solid ${C.line};
  }
  & > li > button {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 60px;
    padding: 10px 14px;
    box-sizing: border-box;
    border: 0;
    background: ${C.card};
    color: ${C.fg};
    text-align: left;
    cursor: pointer;
  }
  & > li > button > div {
    flex: 1;
    min-width: 0;
  }
  & strong {
    display: block;
    font-size: 16px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & span:not([class]) {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: ${C.dim};
  }
`;

export { Ghost };
